import React, { useContext, useEffect, useState, useRef } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import api from '../axiosInstance';
import AuthContext from '../AuthContext';
import { LanguageContext } from '../LanguageContext';
import useWafCaptcha from '../hooks/useWafCaptcha';
import useCapsLockWarning from '../hooks/useCapsLockWarning';
import CapsLockWarning from '../components/CapsLockWarning';

function Login() {
  const { setIsAuthenticated, setUser, setAuthorizationError } = useContext(AuthContext);
  const { language } = useContext(LanguageContext);
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [error, setError] = useState('');
  const [challengeSession, setChallengeSession] = useState(null);
  const [newPassword, setNewPassword] = useState('');
  const [confirmNewPassword, setConfirmNewPassword] = useState('');
  const [isCompletingChallenge, setIsCompletingChallenge] = useState(false);
  const navigate = useNavigate();
  const location = useLocation();
  const errorRef = useRef(null);
  const { ensureCaptcha, CaptchaOverlay } = useWafCaptcha();
  const capsPassword = useCapsLockWarning();
  const capsNewChallenge = useCapsLockWarning();
  const capsConfirmChallenge = useCapsLockWarning();

  const program = location.state && location.state.program ? location.state.program : 'ISET';

  useEffect(() => {
    window.scrollTo(0, 0);
    const el = document.getElementById('page-overview');
    if (el) {
      try { el.focus(); } catch (_) { /* no-op */ }
    }
  }, []);

  useEffect(() => {
    if (error && errorRef.current) {
      errorRef.current.focus();
    }
  }, [error]);

  const handleInputChange = (e) => {
    setEmail(e.target.value);
  };

  const establishSessionAndRedirect = async (emailForSession) => {
    sessionStorage.setItem('post_auth_program', program);
    sessionStorage.setItem('post_auth_path', program === 'DynamicTest' ? '/iset-wizard' : program === 'Jordan' ? '/start-jordan' : '/dashboard');

    let applicantAccountDenied = false;
    const attemptMe = async (delayMs) => {
      if (delayMs) await new Promise(r => setTimeout(r, delayMs));
      try {
        const meResp = await api.get('/api/me');
        if (meResp.data?.authenticated && meResp.data?.id && meResp.data?.isApplicantPortalUser === true) {
          setIsAuthenticated(true);
          setUser({ id: meResp.data.id, email: meResp.data.email || emailForSession });
          setAuthorizationError?.(null);
          return true;
        }
        if (meResp.data?.authenticated && meResp.data?.id) applicantAccountDenied = true;
      } catch (_) {}
      return false;
    };

    const established = await attemptMe(0) || await attemptMe(150) || await attemptMe(400);
    if (!established) {
      setIsAuthenticated(false);
      setUser(null);
      setAuthorizationError?.(applicantAccountDenied ? 'applicant_account_required' : null);
      setError(applicantAccountDenied
        ? (language === 'fr'
          ? 'Cette section nécessite un compte du portail des demandeurs.'
          : 'This area requires an applicant portal account.')
        : (language === 'fr'
          ? 'La session n’a pas pu être établie. Veuillez réessayer.'
          : 'Login succeeded but session not established. Please retry.'));
      return false;
    }

    const stateFrom = (location.state && location.state.from) || '';
    const rawLastPath = sessionStorage.getItem('last_path') || '';
    const lastPath = rawLastPath === '/' ? '' : rawLastPath;
    const programPath = sessionStorage.getItem('post_auth_path') || '/dashboard';
    const disallowed = new Set(['/login', '/register', '/sign-out', '']);
    let dest = '/';
    if (stateFrom && !disallowed.has(stateFrom)) dest = stateFrom;
    else if (lastPath && !disallowed.has(lastPath)) dest = lastPath;
    else if (programPath && !disallowed.has(programPath)) dest = programPath;
    navigate(dest, { replace: true });
    return true;
  };

  const handleLogin = async (e) => {
    e.preventDefault();
    setError('');
    setChallengeSession(null);
    try {
      await ensureCaptcha();
      const resp = await api.post('/api/auth/password-login', { email, password });
      if (!resp.data.success) {
        setError(resp.data.message || (language === 'fr' ? 'Échec de la connexion.' : 'Login failed.'));
        return;
      }
      await establishSessionAndRedirect(email);
    } catch (err) {
      if (err?.response?.status === 409 && err?.response?.data?.challenge === 'NEW_PASSWORD_REQUIRED') {
        setChallengeSession(err.response.data.session || null);
        setNewPassword('');
        setConfirmNewPassword('');
        setError(language === 'fr'
          ? 'Vous devez définir un nouveau mot de passe avant de continuer.'
          : 'You need to set a new password before continuing.');
        return;
      }
      setError(err.response?.data?.message || (language === 'fr' ? 'Échec de la connexion.' : 'Login failed.'));
    }
  };

  const handleCompleteNewPassword = async (e) => {
    e.preventDefault();
    if (!challengeSession) return;
    setError('');
    if (newPassword.length < 8) {
      setError(language === 'fr' ? 'Le mot de passe doit comporter au moins 8 caractères.' : 'Password must be at least 8 characters.');
      return;
    }
    if (newPassword !== confirmNewPassword) {
      setError(language === 'fr' ? 'Les mots de passe ne correspondent pas.' : 'Passwords do not match.');
      return;
    }
    try {
      setIsCompletingChallenge(true);
      await ensureCaptcha();
      const resp = await api.post('/api/auth/complete-new-password', { email, newPassword, session: challengeSession });
      if (!resp.data?.success) {
        setError(resp.data?.message || (language === 'fr' ? 'Impossible de mettre à jour le mot de passe.' : 'Could not set new password.'));
        return;
      }
      setChallengeSession(null);
      setNewPassword('');
      setConfirmNewPassword('');
      await establishSessionAndRedirect(email);
    } catch (err) {
      setError(err.response?.data?.message || (language === 'fr' ? 'Impossible de mettre à jour le mot de passe.' : 'Could not set new password.'));
    } finally {
      setIsCompletingChallenge(false);
    }
  };

  const handleCancelChallenge = () => {
    setChallengeSession(null);
    setNewPassword('');
    setConfirmNewPassword('');
    setError('');
  };

  const text = {
    en: {
      signIn: 'Sign In',
      signInHint: 'Sign in with the email address you used to create your account. If you do not have an account yet, you can create one to track applications, receive updates, and use secure messaging.',
      email: 'Email address for sign-in',
      password: 'Password',
      signInButton: 'Sign In',
      cancelButton: 'Cancel',
      register: "If you do not have an account yet, <a href='/register' class='govuk-link'>create one here</a>.",
      challengeHeading: 'Set a new password to continue',
      challengeIntro: 'A new password is required for this account before you can continue. Choose a password you will remember and keep private.',
      newPassword: 'New password',
      confirmNewPassword: 'Confirm new password',
      submitNewPassword: 'Update password and continue',
      cancelChallenge: 'Back to sign-in'
    },
    fr: {
      signIn: 'Se connecter',
      signInHint: "Connectez-vous avec l'adresse courriel utilisée pour créer votre compte. Si vous n'avez pas encore de compte, vous pouvez en créer un pour suivre vos demandes, recevoir des mises à jour et utiliser la messagerie sécurisée.",
      email: 'Adresse courriel pour la connexion',
      password: 'Mot de passe',
      signInButton: 'Se connecter',
      cancelButton: 'Annuler',
      register: "Si vous n'avez pas encore de compte, <a href='/register' class='govuk-link'>créez-en un ici</a>.",
      challengeHeading: 'Définissez un nouveau mot de passe pour continuer',
      challengeIntro: 'Un nouveau mot de passe est requis pour ce compte avant de pouvoir continuer. Choisissez un mot de passe dont vous vous souviendrez et gardez-le confidentiel.',
      newPassword: 'Nouveau mot de passe',
      confirmNewPassword: 'Confirmer le nouveau mot de passe',
      submitNewPassword: 'Mettre à jour le mot de passe et continuer',
      cancelChallenge: 'Retour à la connexion'
    }
  };

  const content = language === 'fr' ? text.fr : text.en;

  return (
    <>
      <CaptchaOverlay />
      <a href="#main-content" className="govuk-skip-link">
        {language === 'fr' ? 'Passer au contenu principal' : 'Skip to main content'}
      </a>
      <div>
        <main className="govuk-main-wrapper govuk-width-container" role="main" id="main-content" tabIndex="-1">
          <div id="page-overview" style={{ position: 'absolute', left: '-9999px' }} tabIndex="-1">
            {language === 'fr'
              ? "Vous êtes sur la page de connexion. Si vous avez déjà un compte, connectez-vous avec l'adresse courriel utilisée lors de votre inscription."
              : 'You are on the sign-in page. If you already have an account, sign in using the email address you used to register.'}
          </div>
          <h1 className="govuk-heading">{content.signIn}</h1>
          <p className="govuk-hint">
            {content.signInHint}{' '}
            <a href="/privacy" className="govuk-link" target="_blank" rel="noopener noreferrer">
              {language === 'fr' ? 'politique de confidentialité' : 'Privacy Policy'}
            </a>.
          </p>

          {error && (
            <p className="govuk-error-message" aria-live="assertive" tabIndex="-1" ref={errorRef}>
              {error}
            </p>
          )}

          {challengeSession && (
            <form onSubmit={handleCompleteNewPassword} autoComplete="on" className="govuk-!-margin-bottom-6">
              <div className="govuk-inset-text">
                <strong>{content.challengeHeading}</strong>
                <div>{content.challengeIntro}</div>
              </div>
              <div className="govuk-form-group">
                <label className="govuk-label" htmlFor="new-password-challenge">{content.newPassword}</label>
                <input
                  className="govuk-input"
                  id="new-password-challenge"
                  name="new-password-challenge"
                  type="password"
                  autoComplete="new-password"
                  required
                  minLength={8}
                  value={newPassword}
                  onChange={(e) => setNewPassword(e.target.value)}
                  aria-label={content.newPassword}
                  {...capsNewChallenge.bind}
                />
                <CapsLockWarning show={capsNewChallenge.show} language={language} />
              </div>
              <div className="govuk-form-group">
                <label className="govuk-label" htmlFor="confirm-new-password-challenge">{content.confirmNewPassword}</label>
                <input
                  className="govuk-input"
                  id="confirm-new-password-challenge"
                  name="confirm-new-password-challenge"
                  type="password"
                  autoComplete="new-password"
                  required
                  minLength={8}
                  value={confirmNewPassword}
                  onChange={(e) => setConfirmNewPassword(e.target.value)}
                  aria-label={content.confirmNewPassword}
                  {...capsConfirmChallenge.bind}
                />
                <CapsLockWarning show={capsConfirmChallenge.show} language={language} />
              </div>
              <div className="govuk-button-group">
                <button type="submit" className="govuk-button" disabled={isCompletingChallenge}>
                  {isCompletingChallenge ? `${content.submitNewPassword}...` : content.submitNewPassword}
                </button>
                <button type="button" className="govuk-button govuk-button--secondary" onClick={handleCancelChallenge}>
                  {content.cancelChallenge}
                </button>
              </div>
            </form>
          )}

          <form onSubmit={handleLogin} autoComplete="on">
            <div className="govuk-form-group">
              <label className="govuk-label" htmlFor="email">{content.email}</label>
              <input
                className="govuk-input"
                id="email"
                name="email"
                type="email"
                value={email}
                onChange={handleInputChange}
                required
                aria-label={content.email}
                autoComplete="username"
                readOnly={!!challengeSession}
              />
            </div>
            <div className="govuk-form-group">
              <label className="govuk-label" htmlFor="password">{content.password}</label>
              <input
                className="govuk-input"
                id="password"
                name="password"
                type="password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                required
                autoComplete="current-password"
                disabled={!!challengeSession}
                {...capsPassword.bind}
              />
              <CapsLockWarning show={capsPassword.show} language={language} />
              <div style={{ marginTop: '0.5em' }}>
                <a href="/forgot-password" className="govuk-link">
                  {language === 'fr' ? 'Mot de passe oublié ?' : 'Forgot password?'}
                </a>
              </div>
            </div>
            <div className="govuk-button-group">
              <button type="submit" className="govuk-button" disabled={!!challengeSession}>{content.signInButton}</button>
              <a href="/" className="govuk-button govuk-button--secondary">{content.cancelButton}</a>
            </div>
          </form>

          <p className="govuk-body" dangerouslySetInnerHTML={{ __html: content.register }} />
        </main>
      </div>
    </>
  );
}

export default Login;
